import React from "react"
import { Link, useLocation } from "react-router-dom"
import { LayoutDashboard, History, FileText, User, LogOut, Clock, Briefcase, Users, Building2, CreditCard, Sparkles, FileSignature, FileCheck } from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/components/auth-context"

import { useTranslation } from "react-i18next"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"

export function Sidebar() {
    const location = useLocation()
    const { user, userRole, organization, signOut } = useAuth()
    const { t } = useTranslation()
    const isAdminOrOwner = userRole === 'admin' || userRole === 'owner'

    const sidebarItems: { icon: React.ElementType; labelKey: string; href: string; badge?: string }[] = [
        { icon: LayoutDashboard, labelKey: "sidebar.home", href: "/" },
        { icon: Briefcase, labelKey: "sidebar.projects", href: "/projects" },
        { icon: Building2, labelKey: "sidebar.clients", href: "/clients" },
        { icon: FileSignature, labelKey: "sidebar.quotes", href: "/quotes", badge: "Pro" },
        {icon: Users, labelKey: "sidebar.team", href: "/team", badge: "Teams" },
        ...(isAdminOrOwner ? [{ icon: FileCheck, labelKey: "sidebar.approvals", href: "/approvals", badge: "Teams" }] : []),
        { icon: History, labelKey: "sidebar.history", href: "/history" },
        { icon: FileText, labelKey: "sidebar.reports", href: "/reports", badge: "Pro" },
        { icon: Sparkles, labelKey: "sidebar.aiAssistant", href: "/ai-assistant", badge: "Pro" },
        { icon: CreditCard, labelKey: "sidebar.billing", href: "/billing" },
        { icon: User, labelKey: "sidebar.profile", href: "/profile" },
    ]

    const personalSpaceLabel = organization?.type === 'personal'
        ? (user?.user_metadata?.full_name
            ? t('sidebar.personalSpace', { name: user.user_metadata.full_name.split(' ')[0] })
            : t('sidebar.mySpace'))
        : organization?.name

    return (
        <aside className="w-64 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white hidden lg:flex flex-col p-4 m-4 rounded-[2rem] shadow-xl border border-zinc-200 dark:border-zinc-800 transition-colors duration-300 min-h-0 overflow-x-hidden">
            <div className="flex items-center space-x-2 px-4 py-4 shrink-0">
                <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center shadow-md shadow-primary/20">
                    <Clock className="h-5 w-5 text-black" />
                </div>
                <div className="flex flex-col">
                    <span className="text-xl font-bold tracking-wider">tempora</span>
                    {organization && (
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-semibold truncate max-w-[140px]">
                            {personalSpaceLabel}
                        </span>
                    )}
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto overflow-x-hidden space-y-1 mt-4 scrollbar-hide min-h-0 min-w-0">
                {sidebarItems.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            className={cn(
                                "flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group relative min-w-0",
                                isActive
                                    ? "bg-zinc-900 text-white dark:bg-white dark:text-black shadow-lg"
                                    : "text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800"
                            )}
                        >
                            <item.icon className={cn("h-5 w-5 shrink-0", isActive ? "text-primary" : "text-current")} />
                            <span className="font-medium flex-1 truncate">{t(item.labelKey)}</span>
                            {item.badge && !isActive && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary leading-none">
                                    {item.badge}
                                </span>
                            )}
                            {isActive && (
                                <div className="absolute right-3 w-2 h-2 rounded-full bg-primary" />
                            )}
                        </Link>
                    )
                })}
            </nav>

            {/* Language Switcher + Sign Out — always visible at bottom */}
            <div className="shrink-0 mt-2">
                <LanguageSwitcher className="mb-2 mx-1" />
                <button
                    onClick={signOut}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-zinc-500 dark:text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors"
                >
                    <LogOut className="h-5 w-5" />
                    <span>{t('sidebar.signOut')}</span>
                </button>
            </div>
        </aside>
    )
}
